// WizardConnect wrapper for the Potidaea mockup.
// Phase 1 responsibility: pair with a wallet, receive its receive xpub,
// derive the first receive address, expose a subscribable "session" observable.
// Sign requests (Phase 2) will land here too but are out of scope for this ship.

import { EventEmitter } from 'eventemitter3';
import { initiateDappRelay, type DappRelayResult, type WalletReadyMessage } from '@wizardconnect/core';
import { DappConnectionManager } from '@wizardconnect/dapp';
import { cashaddrFromPubkey, type Network } from './derive';

export type ConnectionPhase = 'idle' | 'awaiting-scan' | 'paired' | 'error';

export interface SessionSnapshot {
  phase: ConnectionPhase;
  uri: string | null;         // wiz://…  — show as QR + copy button
  qrUri: string | null;       // same but sometimes formatted with a hint prefix (see core)
  walletName: string | null;
  cashaddr: string | null;
  error: string | null;
}

const initialSnapshot: SessionSnapshot = {
  phase: 'idle', uri: null, qrUri: null, walletName: null, cashaddr: null, error: null,
};

export interface WcEvents {
  change: [snap: SessionSnapshot];
}

export class WcClient extends EventEmitter<WcEvents> {
  private manager: DappConnectionManager | null = null;
  private relay: DappRelayResult | null = null;
  private snap: SessionSnapshot = { ...initialSnapshot };
  readonly network: Network;
  readonly dappName: string;
  readonly dappIcon?: string;

  constructor(opts: { network: Network; dappName: string; dappIcon?: string }) {
    super();
    this.network = opts.network;
    this.dappName = opts.dappName;
    this.dappIcon = opts.dappIcon;
  }

  get snapshot(): SessionSnapshot { return { ...this.snap }; }
  private update(patch: Partial<SessionSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    this.emit('change', { ...this.snap });
  }

  /** Start a fresh pairing session. Emits `change` with `phase: 'awaiting-scan'`
   *  as soon as the wiz:// URI is ready. Idempotent — a second call while a
   *  session is live just returns the current snapshot. */
  connect(): SessionSnapshot {
    if (this.manager) return { ...this.snap };
    this.update({ phase: 'awaiting-scan', error: null });

    const manager = new DappConnectionManager(this.dappName, this.dappIcon);
    this.manager = manager;

    manager.on('walletready', (msg: WalletReadyMessage) => this.onWalletReady(msg));
    manager.on('disconnect', (_reason, message) => this.onDisconnect(message ?? 'wallet disconnected'));

    try {
      const relay = initiateDappRelay((client, status) => manager.updateConnection(client, status));
      this.relay = relay;
      manager.attachRelay(relay);
      this.update({ uri: relay.uri, qrUri: relay.qrUri });
    } catch (e) {
      this.onError(e instanceof Error ? e.message : String(e));
    }
    return { ...this.snap };
  }

  private onWalletReady(msg: WalletReadyMessage): void {
    // The manager auto-populated pubkeyState from wallet_ready.session.paths.
    // We only need the receive path (childIndex 0, index 0) for Phase 1.
    const pubkey = this.manager?.getPubkey(0, 0n);
    if (!pubkey) {
      this.onError('wallet did not deliver a receive xpub');
      return;
    }
    try {
      const cashaddr = cashaddrFromPubkey(pubkey, this.network);
      this.update({ phase: 'paired', walletName: msg.wallet_name ?? 'wallet', cashaddr, error: null });
    } catch (e) {
      this.onError('cashaddr derivation failed: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  private onDisconnect(reason: string): void {
    this.cleanup();
    this.snap = { ...initialSnapshot };
    if (reason) this.snap.error = 'Disconnected: ' + reason;
    this.emit('change', { ...this.snap });
  }

  private onError(msg: string): void {
    this.cleanup();
    this.snap = { ...initialSnapshot, phase: 'error', error: msg };
    this.emit('change', { ...this.snap });
  }

  disconnect(): void {
    this.onDisconnect('user disconnect');
  }

  /** Send a WC sign_transaction_request to the paired wallet. Resolves with the
   *  signed transaction hex. Throws if not paired or if the wallet rejects.
   *
   *  `inputPaths` is [inputIndex, pathName ('receive'|'change'), addressIndex]
   *  telling the wallet which HD child to sign each of ITS inputs with. The
   *  contract input (index 0) is not in this list — the covenant script provides
   *  its own unlock. */
  async signTransaction(request: {
    transaction: string;
    sourceOutputs: unknown[];
    broadcast?: boolean;
    userPrompt?: string;
    inputPaths: [number, string, bigint][];
  }): Promise<string> {
    if (!this.manager) throw new Error('WizardConnect: not paired');
    const { inputPaths, ...tx } = request;
    const res = await this.manager.signTransaction(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { transaction: tx as any, inputPaths },
    );
    if (res.error) throw new Error('wallet: ' + res.error);
    return res.signedTransaction;
  }

  private cleanup(): void {
    try { this.manager?.sendDisconnect('bye').catch(() => {}); } catch { /* noop */ }
    try { this.relay?.cleanup(); } catch { /* noop */ }
    this.manager = null;
    this.relay = null;
  }
}
